// Operator user manual for Dekon SSO, shown on the in-app Help page (/help).
//
// ⚠️ When you change anything in the operator-facing UI (forms, fields, flows,
// routes, labels), you MUST revise the matching section here — in BOTH languages —
// and bump HELP_UPDATED. See the rule in CLAUDE.md. The Localized types below
// force every piece of copy to carry an nb + en value, so the two stay in lock-step.

export type HelpLang = 'nb' | 'en';

/** A single string in both languages. */
export type Localized = Record<HelpLang, string>;
/** A list of strings (e.g. numbered steps) in both languages. */
export type LocalizedList = Record<HelpLang, string[]>;

export type HelpBlock =
    | { kind: 'p'; text: Localized } // paragraph
    | { kind: 'steps'; items: LocalizedList } // numbered steps (ordered list)
    | { kind: 'list'; items: LocalizedList } // bullet list
    | { kind: 'note'; text: Localized } // info callout
    | { kind: 'warn'; text: Localized } // warning callout
    | { kind: 'download'; href: string; filename: string; label: Localized }; // download link/button

export interface HelpSection {
    id: string;
    title: Localized;
    blocks: HelpBlock[];
}

/** Bump this (YYYY-MM-DD) whenever the manual is revised. */
export const HELP_UPDATED = '2026-06-27';

export const helpSections: HelpSection[] = [
    {
        id: 'intro',
        title: { nb: 'Om Dekon SSO', en: 'About Dekon SSO' },
        blocks: [
            {
                kind: 'p',
                text: {
                    nb: 'Dekon SSO er den sentrale innloggingen som gir brukere tilgang til tjenester som SmartStore/Min Butikk og rapportering (Jasper). Denne veiledningen er for operatører som registrerer og forvalter brukere og koblinger.',
                    en: 'Dekon SSO is the central sign-in that gives users access to services such as SmartStore/Min Butikk and reporting (Jasper). This guide is for operators who register and manage users and mappings.',
                },
            },
            {
                kind: 'list',
                items: {
                    nb: [
                        'Lokal bruker – en konto som opprettes her i SSO-en (kunder/eksterne). Får e-post for å sette passord.',
                        'AD-bruker – en intern Dekon-konto fra Active Directory. Logger inn med AD-passordet; speiles automatisk i SSO-en.',
                        'Kobling – knytter en SSO-bruker til en konkret tjeneste (SmartStore- eller Jasper-instans) med riktige påloggingsdetaljer.',
                        'To-faktor (2FA) – ekstra bekreftelse ved innlogging via Authy-app, SMS eller e-postkode.',
                    ],
                    en: [
                        'Local user – an account created here in the SSO (customers/external). Receives an email to set a password.',
                        'AD user – an internal Dekon account from Active Directory. Signs in with the AD password; mirrored automatically in the SSO.',
                        'Mapping – links an SSO user to a specific service (a SmartStore or Jasper instance) with the right credentials.',
                        'Two-factor (2FA) – an extra confirmation at sign-in via the Authy app, SMS or an email code.',
                    ],
                },
            },
            {
                kind: 'note',
                text: {
                    nb: 'Velg språk (norsk/engelsk) og lyst/mørkt tema oppe til høyre. Denne hjelpesiden følger språkvalget ditt.',
                    en: 'Pick language (Norwegian/English) and light/dark theme in the top-right. This help page follows your language choice.',
                },
            },
        ],
    },
    {
        id: 'login',
        title: { nb: 'Logge inn', en: 'Signing in' },
        blocks: [
            {
                kind: 'steps',
                items: {
                    nb: [
                        'Åpne SSO-en og skriv inn brukernavn (e-post eller AD-brukernavn) og passord.',
                        'Etter flere mislykkede forsøk vises en «Jeg er ikke en robot»-bekreftelse – fullfør den.',
                        'Velg to-faktor-metode: godkjenn i Authy-appen, få kode på SMS, eller få en kode på e-post.',
                        'Godkjenn varselet eller skriv inn koden. Du er nå innlogget.',
                    ],
                    en: [
                        'Open the SSO and enter your username (email or AD username) and password.',
                        'After several failed attempts a “I am not a robot” check appears – complete it.',
                        'Choose a two-factor method: approve in the Authy app, get a code by SMS, or get a code by email.',
                        'Approve the prompt or enter the code. You are now signed in.',
                    ],
                },
            },
            {
                kind: 'note',
                text: {
                    nb: 'Lokale brukere bruker passordet de satte via aktiverings-e-posten. AD-brukere bruker sitt vanlige Dekon/AD-passord. «Glemt passord?» på innloggingssiden sender en ny lenke.',
                    en: 'Local users use the password they set via the activation email. AD users use their normal Dekon/AD password. “Forgot password?” on the sign-in page sends a new link.',
                },
            },
        ],
    },
    {
        id: 'register',
        title: { nb: 'Registrere en ny bruker', en: 'Registering a new user' },
        blocks: [
            {
                kind: 'p',
                text: {
                    nb: 'Åpne «Registrer bruker» fra menyen og hold deg på fanen «Registrer».',
                    en: 'Open “Register user” from the menu and stay on the “Register” tab.',
                },
            },
            {
                kind: 'steps',
                items: {
                    nb: [
                        'Velg kunde fra «Velg kunde» (lista er søkbar). Mangler kunden? Synkroniser først (se «Synkronisere kunder»).',
                        'Fyll inn «Fornavn» og «Etternavn».',
                        'Fyll inn «Mobilnummer» – velg land i nedtrekket (standard Norge, +47) og skriv nummeret. Du kan søke i landlista på navn eller landkode.',
                        'Fyll inn «E-post». Denne blir også brukernavnet til brukeren.',
                        'Valgfritt: kryss av «Send samme lenke til meg» hvis du selv vil motta aktiveringslenken.',
                        'Valgfritt: kryss av «Bruk ny meny-kobling» hvis brukeren skal ha den nye SmartStore-menyen.',
                        'Klikk «Registrer bruker». Brukeren får en e-post for å sette passord og aktivere kontoen.',
                    ],
                    en: [
                        'Choose the customer in “Select customer” (the list is searchable). Customer missing? Sync first (see “Syncing customers”).',
                        'Fill in “First name” and “Last name”.',
                        'Fill in “Mobile number” – pick the country in the dropdown (defaults to Norway, +47) and type the number. You can search the country list by name or dial code.',
                        'Fill in “Email”. This also becomes the user’s username.',
                        'Optional: tick “Send the same link to me” to also receive the activation link yourself.',
                        'Optional: tick “Use new menu mapping” if the user should get the new SmartStore menu.',
                        'Click “Register user”. The user receives an email to set a password and activate the account.',
                    ],
                },
            },
            {
                kind: 'note',
                text: {
                    nb: 'Dette oppretter en lokal SSO-bruker – ikke en Active Directory-bruker. Brukeren får tilgang til tjenester først når du har lagt til koblinger (se «SmartStore-koblinger» / «Rapporterings-koblinger»).',
                    en: 'This creates a local SSO user – not an Active Directory user. The user only gets access to services once you add mappings (see “SmartStore mappings” / “Reporting mappings”).',
                },
            },
        ],
    },
    {
        id: 'edit-delete',
        title: { nb: 'Redigere eller slette en bruker', en: 'Editing or deleting a user' },
        blocks: [
            {
                kind: 'steps',
                items: {
                    nb: [
                        'På «Registrer bruker», bytt til «Rediger».',
                        'Velg brukeren fra lista. Feltene fylles ut automatisk.',
                        'Gjør endringene og klikk «Lagre endringer».',
                        'For å slette: åpne brukeren i «Rediger», klikk «Slett bruker» og bekreft.',
                    ],
                    en: [
                        'On “Register user”, switch to “Edit”.',
                        'Pick the user from the list. The fields fill in automatically.',
                        'Make your changes and click “Save changes”.',
                        'To delete: open the user under “Edit”, click “Delete user” and confirm.',
                    ],
                },
            },
            {
                kind: 'warn',
                text: {
                    nb: 'Endrer du e-post eller mobilnummer, blir to-faktor satt opp på nytt, og ved ny e-post må brukeren bekrefte adressen på nytt. Sletting fjerner den lokale kontoen og alle koblingene til brukeren – dette kan ikke angres.',
                    en: 'If you change the email or mobile number, two-factor is set up again, and for a new email the user must re-confirm the address. Deleting removes the local account and all of the user’s mappings – this cannot be undone.',
                },
            },
        ],
    },
    {
        id: 'csv',
        title: { nb: 'Importere brukere fra CSV', en: 'Importing users from CSV' },
        blocks: [
            {
                kind: 'p',
                text: {
                    nb: 'Med «Importer»-fanen kan du registrere mange brukere og koblinger på én gang fra en CSV-fil med overskriftsrad. Last ned malen under, fyll inn én rad per kobling, og lagre som CSV (Excel kan åpne og lagre filen).',
                    en: 'The “Import” tab lets you register many users and mappings at once from a CSV file with a header row. Download the template below, fill in one row per mapping, and save as CSV (Excel can open and save the file).',
                },
            },
            {
                kind: 'download',
                href: '/dekon-sso-importmal.csv',
                filename: 'dekon-sso-importmal.csv',
                label: { nb: 'Last ned CSV-mal', en: 'Download CSV template' },
            },
            {
                kind: 'warn',
                text: {
                    nb: 'Eksempelradene i malen (Ola/Kari) viser kun formatet – kunde-IDer, e-poster og telefonnumre er oppdiktet. Bytt dem ut med ekte data før import. Importerer du eksemplene som de er, finnes ikke kundene, og radene rapporteres i resultattabellen («Customer … not found»).',
                    en: 'The example rows in the template (Ola/Kari) only show the format – the customer IDs, emails and phone numbers are made up. Replace them with real data before importing. Importing the examples as-is fails because the customers do not exist, and those rows are reported in the results table (“Customer … not found”).',
                },
            },
            {
                kind: 'note',
                text: {
                    nb: 'Kolonner: first name, last name, country code, mobile number, email, customerId, og koblingsfelt: ssApplicationServer, ssCompany, ssCompanyPwd, ssPwd, ssStoreId, ssUser, isJasper, jasperApplicationName. Hver rad må ha en kobling – ssApplicationServer (SmartStore) eller jasperApplicationName (Jasper) – ellers hoppes raden over. Firmapassord (ssCompanyPwd) kan stå tomt.',
                    en: 'Columns: first name, last name, country code, mobile number, email, customerId, and mapping fields: ssApplicationServer, ssCompany, ssCompanyPwd, ssPwd, ssStoreId, ssUser, isJasper, jasperApplicationName. Each row must include a mapping – ssApplicationServer (SmartStore) or jasperApplicationName (Jasper) – or the row is skipped. The company password (ssCompanyPwd) may be left blank.',
                },
            },
            {
                kind: 'steps',
                items: {
                    nb: [
                        'Bytt til «Importer» og last opp CSV-fila.',
                        'Kontroller forhåndsvisningen: felt med feil markeres med rødt (hold over for å se hva som er galt). Rett verdiene direkte i tabellen – kunde, SmartStore-instans, Jasper-server og landkode velges fra søkbare lister.',
                        'Fjern avkrysning på rader du ikke vil importere.',
                        'Klikk «Importer». Er alt i orden, registreres alle radene.',
                        'Er det feil på én eller flere rader, stoppes hele importen – ingenting lagres. Resultattabellen viser radnummer og årsak. Rett fila og kjør hele importen på nytt.',
                    ],
                    en: [
                        'Switch to “Import” and upload the CSV file.',
                        'Check the preview: fields with an error are highlighted in red (hover to see what is wrong). Correct the values directly in the table – customer, SmartStore instance, Jasper server and country code are chosen from searchable lists.',
                        'Untick any rows you do not want to import.',
                        'Click “Import”. If everything is in order, all rows are registered.',
                        'If one or more rows have an error, the whole import is stopped – nothing is saved. The results table shows the row number and reason. Fix the file and run the whole import again.',
                    ],
                },
            },
            {
                kind: 'p',
                text: {
                    nb: 'Importen er alt-eller-ingenting: er det feil på én eller flere rader, lagres ingenting. Slik fungerer det:',
                    en: 'The import is all-or-nothing: if one or more rows have an error, nothing is saved. Here is how it works:',
                },
            },
            {
                kind: 'list',
                items: {
                    nb: [
                        'Hver rad valideres før noe lagres. Vanlige feil: ukjent kunde, manglende kobling, manglende brukernavn/e-post, eller en kobling brukeren allerede har.',
                        'Eksisterende brukere gjenbrukes (slås opp på e-post), men har brukeren allerede den aktuelle koblingen, regnes raden som feil – slik unngår du duplikater ved ny import.',
                        'Siden ingenting lagres når det finnes feil, kan du trygt rette fila og kjøre hele importen på nytt uten å få halve listen inne fra før.',
                    ],
                    en: [
                        'Every row is validated before anything is saved. Common errors: unknown customer, missing mapping, missing username/email, or a mapping the user already has.',
                        'Existing users are reused (matched by email), but if the user already has that mapping the row counts as an error – so you avoid duplicates on re-import.',
                        'Because nothing is saved when there are errors, you can safely fix the file and run the whole import again without half the list already imported.',
                    ],
                },
            },
        ],
    },
    {
        id: 'resend',
        title: { nb: 'Sende bekreftelse på nytt', en: 'Resending the confirmation' },
        blocks: [
            {
                kind: 'steps',
                items: {
                    nb: [
                        'På «Registrer bruker», bytt til «Send bekreftelse på nytt».',
                        'Søk opp brukeren (navn, e-post eller telefon) og send aktiverings-/bekreftelses-e-posten på nytt.',
                    ],
                    en: [
                        'On “Register user”, switch to “Resend confirmation”.',
                        'Search for the user (by name, email or phone) and resend the activation/confirmation email.',
                    ],
                },
            },
            {
                kind: 'note',
                text: {
                    nb: 'Hvis brukeren allerede har bekreftet kontoen, får du beskjed om det. Be brukeren sjekke søppelpost hvis e-posten ikke dukker opp.',
                    en: 'If the user has already confirmed the account, you are told so. Ask the user to check their spam folder if the email does not arrive.',
                },
            },
        ],
    },
    {
        id: 'sync',
        title: { nb: 'Synkronisere kunder (24SevenOffice)', en: 'Syncing customers (24SevenOffice)' },
        blocks: [
            {
                kind: 'p',
                text: {
                    nb: 'På «Registrer bruker», bytt til «24SevenOffice» og kjør synkronisering. Da hentes den nyeste kundelista fra 24SevenOffice inn i «Velg kunde»-nedtrekket. Gjør dette hvis en kunde mangler når du skal registrere.',
                    en: 'On “Register user”, switch to “24SevenOffice” and run the sync. This pulls the latest customer list from 24SevenOffice into the “Select customer” dropdown. Do this if a customer is missing when you register.',
                },
            },
        ],
    },
    {
        id: 'smartstore',
        title: { nb: 'SmartStore-koblinger', en: 'SmartStore mappings' },
        blocks: [
            {
                kind: 'p',
                text: {
                    nb: 'Åpne «Koblinger» og fanen «SmartStore». En kobling gir en SSO-bruker tilgang til en bestemt SmartStore-installasjon.',
                    en: 'Open “Mappings” and the “SmartStore” tab. A mapping gives an SSO user access to a specific SmartStore installation.',
                },
            },
            {
                kind: 'steps',
                items: {
                    nb: [
                        'I «Legg til»: velg «SmartStore-instans» og «Brukernavn» (SSO-brukerens e-post).',
                        'Fyll inn «SmartStore-bruker (CS10)» og «Passord (CS10)» – brukerens påloggingsdetaljer i SmartStore.',
                        'Fyll inn «Firma (CS00)» og eventuelt «Firmapassord (CS00)» (ofte tomt).',
                        'Fyll inn «Butikk-ID/nummer (BD88)».',
                        'Valgfritt: kryss av «Legg til Jasper-kobling» og velg Jasper-server hvis brukeren også skal ha rapportering.',
                        'Lagre. I «Rediger» velger du bruker og instans, endrer og klikker «Oppdater». «Slett kobling» fjerner koblingen.',
                    ],
                    en: [
                        'In “Add”: choose the “SmartStore instance” and “Username” (the SSO user’s email).',
                        'Fill in “SmartStore user (CS10)” and “Password (CS10)” – the user’s SmartStore credentials.',
                        'Fill in “Company (CS00)” and optionally “Company password (CS00)” (often blank).',
                        'Fill in “Store ID/number (BD88)”.',
                        'Optional: tick “Add Jasper mapping” and pick a Jasper server if the user should also have reporting.',
                        'Save. Under “Edit” you pick the user and instance, make changes and click “Update”. “Delete mapping” removes it.',
                    ],
                },
            },
            {
                kind: 'note',
                text: {
                    nb: 'Passordfeltene er maskerte – bruk «Vis»/«Skjul» for å kontrollere det du skriver. Koden i parentes (CS10, CS00, BD88) viser hvilket SmartStore-felt verdien hører til.',
                    en: 'The password fields are masked – use “Show”/“Hide” to check what you type. The code in parentheses (CS10, CS00, BD88) shows which SmartStore field the value belongs to.',
                },
            },
        ],
    },
    {
        id: 'jasper',
        title: { nb: 'Rapporterings-koblinger (Jasper)', en: 'Reporting mappings (Jasper)' },
        blocks: [
            {
                kind: 'steps',
                items: {
                    nb: [
                        'Åpne «Koblinger» og fanen «Rapportering».',
                        'I «Legg til»: velg «Jasper-serverinstans» og «SSO-brukernavn».',
                        'Fyll inn «Jasper organisasjons-ID», «Jasper-brukernavn» og «Jasper-passord».',
                        'Lagre. «Rediger» og «Slett kobling» fungerer som for SmartStore.',
                    ],
                    en: [
                        'Open “Mappings” and the “Reporting” tab.',
                        'In “Add”: choose the “Jasper server instance” and “SSO username”.',
                        'Fill in “Jasper organization ID”, “Jasper username” and “Jasper password”.',
                        'Save. “Edit” and “Delete mapping” work the same as for SmartStore.',
                    ],
                },
            },
        ],
    },
    {
        id: 'users',
        title: { nb: 'Brukeroversikt', en: 'User overview' },
        blocks: [
            {
                kind: 'p',
                text: {
                    nb: 'Under «Brukere» finner du alle registrerte brukere i en tabell du kan søke i og sortere (klikk på kolonneoverskriftene). Du kan merke én eller flere brukere for sletting.',
                    en: 'Under “Users” you find all registered users in a table you can search and sort (click the column headers). You can select one or more users for deletion.',
                },
            },
            {
                kind: 'warn',
                text: {
                    nb: 'Sletting fra brukeroversikten fjerner kontoene og alle koblingene deres permanent. Kontroller utvalget nøye før du bekrefter.',
                    en: 'Deleting from the user overview permanently removes the accounts and all their mappings. Check the selection carefully before confirming.',
                },
            },
        ],
    },
    {
        id: 'roles',
        title: { nb: 'Roller', en: 'Roles' },
        blocks: [
            {
                kind: 'p',
                text: {
                    nb: 'Under «Roller» kan administratorer endre rollen til en bruker (f.eks. gi eller fjerne admin-tilgang). Velg bruker, velg ny rolle og lagre.',
                    en: 'Under “Roles”, administrators can change a user’s role (e.g. grant or remove admin access). Pick the user, choose the new role and save.',
                },
            },
        ],
    },
    {
        id: 'faq',
        title: { nb: 'Feilsøking', en: 'Troubleshooting' },
        blocks: [
            {
                kind: 'list',
                items: {
                    nb: [
                        'Brukeren fikk ikke aktiverings-e-posten → send den på nytt («Send bekreftelse på nytt») og be brukeren sjekke søppelpost.',
                        '«Det finnes allerede en kobling for denne brukeren» → brukeren har allerede en kobling for den instansen; bruk «Rediger» i stedet.',
                        '«Skriv et gyldig telefonnummer» → kontroller at riktig land er valgt og at nummeret er korrekt.',
                        'Kunden mangler i «Velg kunde» → kjør synkronisering under «24SevenOffice».',
                        'Robot-bekreftelse dukker opp ved innlogging → det skjer etter flere mislykkede forsøk; fullfør den for å fortsette.',
                    ],
                    en: [
                        'The user did not get the activation email → resend it (“Resend confirmation”) and ask the user to check spam.',
                        '“A mapping already exists for this user” → the user already has a mapping for that instance; use “Edit” instead.',
                        '“Enter a valid phone number” → check that the right country is selected and the number is correct.',
                        'The customer is missing from “Select customer” → run the sync under “24SevenOffice”.',
                        'A robot check appears at sign-in → this happens after several failed attempts; complete it to continue.',
                    ],
                },
            },
            {
                kind: 'note',
                text: {
                    nb: 'Finner du ikke svaret her, kontakt systemansvarlig hos Dekon IT.',
                    en: 'If you cannot find the answer here, contact the system administrator at Dekon IT.',
                },
            },
        ],
    },
];
